import { useCallback, useEffect, useRef, useState } from "react";
import { getDraftFileBase64 } from "../services/generatedContractService";
import { openDraftInHost } from "../services/hostDocumentService";
import {
  createGameRuleTemplate,
  detailFieldsToWizardFields,
  getGameRuleTemplate,
  patchGameRuleTemplate,
  putGameRuleTemplateDocument
} from "../services/gameRuleTemplateService";
import {
  hasMalformedDoubleBracketPlaceholders,
  insertDoubleBracketPlaceholder,
  readDocumentBodyPlainText,
  removeDoubleBracketPlaceholdersForKey
} from "../services/doubleBracketWordService";
import {
  exportCurrentDocumentAsUint8Array,
  readDocumentSavedIfSupported
} from "../services/gameRuleWordService";
import type { GameRuleFieldType, GameRuleWizardField } from "../types/gameRuleTemplate";
import { officeErrorToLogObject } from "../utils/officeJsDebugLog";

const LOG_PREFIX = "[gameRuleTemplateWizard]";

export type WizardStep = "form" | "author";

export type EditTemplateLoadStatus = "idle" | "loading" | "ready" | "error";

export const labelToSuggestedKey = (label: string, used: ReadonlySet<string>): string => {
  let raw = label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!raw) {
    raw = "FIELD";
  }
  let candidate = raw;
  let n = 2;
  while (used.has(candidate)) {
    candidate = `${raw}_${n}`;
    n += 1;
  }
  return candidate;
};

/** Default option row only for types that require an option list in the wizard. */
const newFieldNeedsDefaultOptions = (t: GameRuleFieldType): boolean => t === "radio" || t === "select";

export const useCreateGameRuleTemplateWizard = (editTemplateId?: string) => {
  const isEditMode = Boolean(editTemplateId?.trim());
  const [step, setStep] = useState<WizardStep>(() => (isEditMode ? "author" : "form"));
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [storedFileId, setStoredFileId] = useState<string | null>(null);
  const [documentWebUrl, setDocumentWebUrl] = useState<string | null>(null);
  const [fields, setFields] = useState<GameRuleWizardField[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editLoadStatus, setEditLoadStatus] = useState<EditTemplateLoadStatus>(() =>
    isEditMode ? "loading" : "idle"
  );

  useEffect(() => {
    const tid = editTemplateId?.trim();
    if (!tid) {
      setEditLoadStatus("idle");
      return;
    }
    let cancelled = false;
    setEditLoadStatus("loading");
    setError(null);
    void (async () => {
      try {
        const detail = await getGameRuleTemplate(tid);
        if (cancelled) {
          return;
        }
        setTemplateId(detail.id);
        setStoredFileId(detail.storedFileId);
        setDocumentWebUrl(detail.documentWebUrl);
        setName(detail.name);
        setDescription(detail.description ?? "");
        setFields(detailFieldsToWizardFields(detail));
        setStep("author");
        setEditLoadStatus("ready");
        if (detail.storedFileId) {
          try {
            console.info(LOG_PREFIX, "edit mode: getDraftFileBase64 …", {
              templateId: tid,
              storedFileId: detail.storedFileId
            });
            const base64 = await getDraftFileBase64(detail.storedFileId);
            console.info(LOG_PREFIX, "edit mode: getDraftFileBase64 ok", { base64Length: base64.length });
            console.info(LOG_PREFIX, "edit mode: openDraftInHost …");
            await openDraftInHost(base64);
            console.info(LOG_PREFIX, "edit mode: openDraftInHost ok");
          } catch (hostErr: unknown) {
            console.error(LOG_PREFIX, "edit mode: open draft in Word failed", officeErrorToLogObject(hostErr));
            const hostMsg = hostErr instanceof Error ? hostErr.message : "Could not load the draft into Word.";
            setError(
              `${hostMsg} You can still edit fields below. Open the document from SharePoint if needed.`
            );
          }
        }
      } catch (e: unknown) {
        if (cancelled) {
          return;
        }
        setEditLoadStatus("error");
        setError(e instanceof Error ? e.message : "Failed to load template.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editTemplateId]);

  const addField = useCallback(
    (type: GameRuleFieldType) => {
      const id = crypto.randomUUID();
      const label =
        type === "date_picker"
          ? "Start Date"
          : type === "text_input"
            ? "New Field"
            : "New Field";
      setFields((prev) => {
        const key = labelToSuggestedKey(label, new Set(prev.map((p) => p.key)));
        const base: GameRuleWizardField = {
          id,
          key,
          label,
          type,
          required: false,
          options: newFieldNeedsDefaultOptions(type) ? [{ value: "option_1", label: "Option 1" }] : []
        };
        return [...prev, base];
      });
    },
    []
  );

  const removeField = useCallback((id: string) => {
    setFields((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) {
        void removeDoubleBracketPlaceholdersForKey(target.key).catch((e: unknown) => {
          const msg = e instanceof Error ? e.message : "Could not remove placeholder from the document.";
          setError(msg);
        });
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const updateField = useCallback((id: string, patch: Partial<GameRuleWizardField>) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }, []);

  const replaceFields = useCallback((nextFields: GameRuleWizardField[]) => {
    setFields(nextFields);
  }, []);

  const updateFieldLabel = useCallback((id: string, label: string) => {
    setFields((prev) =>
      prev.map((f) => {
        if (f.id !== id) {
          return f;
        }
        const used = new Set(prev.filter((p) => p.id !== id).map((p) => p.key));
        const key = labelToSuggestedKey(label, used);
        return { ...f, label, key };
      })
    );
  }, []);

  const submitUpload = useCallback(async () => {
    setError(null);
    if (!name.trim()) {
      setError("Game rule name is required.");
      return;
    }
    if (!file) {
      setError("Please choose a .docx file.");
      return;
    }
    setBusy(true);
    try {
      const created = await createGameRuleTemplate(name.trim(), description.trim() || undefined, file);
      setTemplateId(created.templateId);
      setStoredFileId(created.storedFileId);
      setDocumentWebUrl(created.documentWebUrl);
      setStep("author");
      try {
        console.info(LOG_PREFIX, "after create: getDraftFileBase64 …", {
          templateId: created.templateId,
          storedFileId: created.storedFileId
        });
        const base64 = await getDraftFileBase64(created.storedFileId);
        console.info(LOG_PREFIX, "after create: getDraftFileBase64 ok", { base64Length: base64.length });
        console.info(LOG_PREFIX, "after create: openDraftInHost …");
        await openDraftInHost(base64);
        console.info(LOG_PREFIX, "after create: openDraftInHost ok");
      } catch (hostErr: unknown) {
        console.error(LOG_PREFIX, "after create: open draft in Word failed", officeErrorToLogObject(hostErr));
        const hostMsg = hostErr instanceof Error ? hostErr.message : "Could not load the draft into Word.";
        setError(
          `${hostMsg} The template was created—you can define fields below. Open the document from SharePoint if needed.`
        );
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Upload failed.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }, [description, file, name]);

  const openSharePointUrl = useCallback(() => {
    if (documentWebUrl) {
      window.open(documentWebUrl, "_blank", "noopener,noreferrer");
    }
  }, [documentWebUrl]);

  const tagField = useCallback(async (key: string) => {
    setError(null);
    try {
      await insertDoubleBracketPlaceholder(key);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Could not insert placeholder.";
      setError(msg);
    }
  }, []);

  const fieldsRef = useRef<GameRuleWizardField[]>(fields);
  fieldsRef.current = fields;
  const templateIdRef = useRef<string | null>(templateId);
  templateIdRef.current = templateId;
  const nameRef = useRef(name);
  const descriptionRef = useRef(description);
  nameRef.current = name;
  descriptionRef.current = description;
  const persistInFlightRef = useRef(false);

  const persistDocumentAndFields = useCallback(async (): Promise<boolean> => {
    const tid = templateIdRef.current;
    if (!tid || persistInFlightRef.current) {
      return false;
    }
    persistInFlightRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const nm = nameRef.current.trim();
      if (!nm) {
        setError("Game rule name is required.");
        return false;
      }
      const bodyText = await readDocumentBodyPlainText();
      if (hasMalformedDoubleBracketPlaceholders(bodyText)) {
        setError("Invalid placeholder format detected.");
        return false;
      }
      await patchGameRuleTemplate(tid, {
        name: nm,
        description: descriptionRef.current.trim() ? descriptionRef.current.trim() : null,
        fields: fieldsRef.current
      });
      const bytes = await exportCurrentDocumentAsUint8Array();
      const copy = Uint8Array.from(bytes);
      const blob = new Blob([copy], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      });
      await putGameRuleTemplateDocument(tid, blob);
      return true;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Save failed.";
      setError(msg);
      return false;
    } finally {
      persistInFlightRef.current = false;
      setBusy(false);
    }
  }, []);

  const saveTemplateDetails = useCallback(async () => {
    const tid = templateIdRef.current;
    if (!tid || persistInFlightRef.current) {
      return;
    }
    const nm = nameRef.current.trim();
    if (!nm) {
      setError("Game rule name is required.");
      return;
    }
    persistInFlightRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await patchGameRuleTemplate(tid, {
        name: nm,
        description: descriptionRef.current.trim() ? descriptionRef.current.trim() : null,
        fields: fieldsRef.current
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Save template details failed.";
      setError(msg);
    } finally {
      persistInFlightRef.current = false;
      setBusy(false);
    }
  }, []);

  /** When Word reports the document transitioned to saved, push field metadata and document bytes together. */
  useEffect(() => {
    if (step !== "author" || !templateId) {
      return;
    }
    let cancelled = false;
    let lastSaved: boolean | null = null;
    const tick = async (): Promise<void> => {
      if (cancelled || persistInFlightRef.current) {
        return;
      }
      const saved = await readDocumentSavedIfSupported();
      if (cancelled || saved === null) {
        return;
      }
      if (lastSaved === false && saved === true) {
        await persistDocumentAndFields();
      }
      lastSaved = saved;
    };
    void tick();
    const id = window.setInterval(() => {
      void tick();
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [persistDocumentAndFields, step, templateId]);

  return {
    step,
    name,
    setName,
    description,
    setDescription,
    file,
    setFile,
    templateId,
    storedFileId,
    documentWebUrl,
    fields,
    error,
    busy,
    isEditMode,
    editLoadStatus,
    addField,
    removeField,
    updateField,
    replaceFields,
    updateFieldLabel,
    submitUpload,
    openSharePointUrl,
    tagField,
    saveTemplateDetails,
    persistDocumentAndFields
  };
};
